<template>
    <div class="datasource-editor">
        <section>
            <h4 class="datasource-title">Event log</h4>
            <div class="datasource-field">
                <label :for="`${idPrefix}connection`">Data connection<span v-if="level === 'workspace'"> *</span></label>
                <Select v-if="level === 'workspace'" :inputId="`${idPrefix}connection`" :modelValue="inputConnectionId"
                        :options="sourceConnections" optionLabel="name" optionValue="id" :disabled="disabled"
                        placeholder="Choose a data connection" :invalid="showErrors && !inputConnectionId"
                        data-cy="datasource-connection"
                        @update:modelValue="changeConnection"/>
                <span v-else data-cy="datasource-connection">
                    {{ inputConnection ? inputConnection.name : 'Not set on the workspace' }}
                    <small class="datasource-help">(chosen on the workspace)</small>
                </span>
                <small v-if="inputConnector" class="datasource-help">{{ inputConnector.title }}</small>
            </div>
            <SchemaForm v-if="sourceSchema" :schema="sourceSchema" :modelValue="inputOptions"
                        :loadOptions="loaderFor(inputConnectionId)" :exclude="excludeOptions" :disabled="disabled"
                        :showErrors="showErrors" :idPrefix="`${idPrefix}input-`"
                        @update:modelValue="setInputOptions"/>
        </section>

        <section v-if="inputConnector">
            <h4 class="datasource-title">Results</h4>
            <div v-if="level === 'graph'" class="datasource-field datasource-inline">
                <ToggleSwitch inputId="datasource-own-output" :modelValue="!inheritsOutput" :disabled="disabled"
                              @update:modelValue="toggleOwnOutput"/>
                <label for="datasource-own-output">Configure results for this graph only</label>
            </div>
            <template v-if="!inheritsOutput">
                <div class="datasource-field">
                    <label :for="`${idPrefix}output-mode`">Publish results</label>
                    <Select :inputId="`${idPrefix}output-mode`" :modelValue="output.mode || 'default'"
                            :options="outputModes" optionLabel="label" optionValue="value" optionDisabled="disabled"
                            :disabled="disabled" data-cy="datasource-output-mode"
                            @update:modelValue="mode => setOutput({mode})"/>
                </div>
                <div v-if="output.mode === 'connection'" class="datasource-field">
                    <label :for="`${idPrefix}output-connection`">Output connection *</label>
                    <Select :inputId="`${idPrefix}output-connection`" :modelValue="output.connection_id"
                            :options="sinkConnections" optionLabel="name" optionValue="id" :disabled="disabled"
                            :invalid="showErrors && !output.connection_id"
                            @update:modelValue="id => setOutput({mode: 'connection', connection_id: id})"/>
                </div>
                <SchemaForm v-if="sinkSchema && output.mode !== 'none'" :schema="sinkSchema"
                            :modelValue="output.options || {}" :loadOptions="loaderFor(outputConnectionId)"
                            :disabled="disabled" :showErrors="showErrors" :idPrefix="`${idPrefix}output-`"
                            @update:modelValue="options => setOutput({...output, options})"/>
            </template>
            <small v-else class="datasource-help">{{ inheritedOutputText }}</small>
        </section>
    </div>
</template>

<script setup>
// Where a workspace or graph reads its event log and publishes its results: a data connection
// plus the connector's options, rendered from the connector's schemas (connectionStore).
// Stored shapes: see app/src/portal_api/service/datasource_service.py.
import {computed, onMounted, watch} from 'vue';
import {useStore} from 'vuex';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import SchemaForm from '@/src/shared-components/SchemaForm.vue';
import {missingRequired, valuesForSubmit} from '@/src/utils/connectorSchema';

const props = defineProps({
    modelValue: {type: Object, default: null},
    level: {type: String, default: 'workspace', validator: value => ['workspace', 'graph'].includes(value)},
    // graph level: the workspace data source the graph inherits from
    workspaceDatasource: {type: Object, default: null},
    // option fields edited elsewhere (Celonis filters have their own editor)
    excludeOptions: {type: Array, default: () => ['filters']},
    disabled: {type: Boolean, default: false},
    showErrors: {type: Boolean, default: false},
    idPrefix: {type: String, default: 'datasource-'},
});

const emit = defineEmits(['update:modelValue', 'validation', 'connector']);

const store = useStore();
onMounted(() => store.dispatch('connectionStore/loadAll'));

const datasource = computed(() => props.modelValue || {});
const connections = computed(() => store.getters['connectionStore/connections']);
const connectorOf = (connectionId) => store.getters['connectionStore/connectorOfConnection'](connectionId);

const inputConnectionId = computed(() => (props.level === 'workspace'
    ? datasource.value.input?.connection_id
    : props.workspaceDatasource?.input?.connection_id) ?? null);
const inputConnection = computed(() => store.getters['connectionStore/connection'](inputConnectionId.value));
const inputConnector = computed(() => connectorOf(inputConnectionId.value));
const sourceSchema = computed(() => inputConnector.value?.source?.options_schema || null);
const sourceConnections = computed(() => connections.value.filter(connection => connectorOf(connection.id)?.source));

const inputOptions = computed(() => (props.level === 'workspace'
    ? datasource.value.input?.options || {}
    : {...(props.workspaceDatasource?.input?.options || {}), ...(datasource.value.input?.options || {})}));

const inheritsOutput = computed(() => props.level === 'graph' && !datasource.value.output);
const canPublishWithInput = computed(() => {
    const sink = inputConnector.value?.sink;
    return Boolean(sink) && (!sink.compatible_sources || sink.compatible_sources.includes(inputConnector.value.type));
});
// Results are always published: with the input connection when its connector can take them,
// otherwise to another connection. ("none" is still read from older data, but not offered.)
const defaultOutput = computed(() => ({mode: canPublishWithInput.value ? 'default' : 'connection'}));
const output = computed(() => datasource.value.output || props.workspaceDatasource?.output || defaultOutput.value);
const sinkConnections = computed(() => connections.value.filter(connection => {
    const sink = connectorOf(connection.id)?.sink;
    return sink && (!sink.compatible_sources || sink.compatible_sources.includes(inputConnector.value?.type));
}));
const outputModes = computed(() => [
    {value: 'default', label: `With the input connection${inputConnection.value ? ` (${inputConnection.value.name})` : ''}`,
     disabled: !canPublishWithInput.value},
    {value: 'connection', label: 'To another data connection', disabled: sinkConnections.value.length === 0},
]);
const outputConnectionId = computed(() => (output.value.mode === 'connection' ? output.value.connection_id : inputConnectionId.value));
const outputConnector = computed(() => (output.value.mode === 'connection'
    ? connectorOf(output.value.connection_id) : inputConnector.value));
const sinkSchema = computed(() => outputConnector.value?.sink?.options_schema || null);

const inheritedOutputText = computed(() => {
    const mode = output.value.mode || 'default';
    if (mode === 'none') return 'As configured on the workspace: results are not published.';
    if (mode === 'connection') return 'As configured on the workspace: published to another data connection.';
    return 'As configured on the workspace: published with the input connection.';
});

const loaderFor = (connectionId) => (connectionId
    ? (provider, params) => store.dispatch('connectionStore/loadOptions', {connectionId, provider, params})
    : null);

const emitValue = (value) => emit('update:modelValue', value);

const changeConnection = (connectionId) => {
    emitValue({...datasource.value, input: {connection_id: connectionId, options: {}}});
};

const setInputOptions = (options) => {
    const input = props.level === 'workspace' ? {...datasource.value.input, options} : {options};
    emitValue({...datasource.value, input});
};

const setOutput = (changes) => {
    const next = {...output.value, ...changes};
    if (changes.mode && changes.mode !== output.value.mode) {
        next.options = {};
        if (changes.mode !== 'connection') delete next.connection_id;
    }
    emitValue({...datasource.value, output: next});
};

const toggleOwnOutput = (own) => {
    const value = {...datasource.value};
    if (own) {
        value.output = {...(props.workspaceDatasource?.output || defaultOutput.value)};
    } else {
        delete value.output;
    }
    emitValue(value);
};

// What still has to be filled in, for the dialog's save button.
const missing = computed(() => {
    const result = [];
    if (!inputConnectionId.value) result.push('connection');
    if (sourceSchema.value) {
        result.push(...missingRequired(sourceSchema.value, inputOptions.value)
            .filter(path => !props.excludeOptions.includes(path.split('.')[0])));
    }
    if (!inheritsOutput.value && output.value.mode !== 'none') {
        if (output.value.mode === 'connection' && !output.value.connection_id) result.push('output.connection');
        if (sinkSchema.value) result.push(...missingRequired(sinkSchema.value, output.value.options || {}).map(path => `output.${path}`));
    }
    return result;
});

watch(missing, value => emit('validation', value), {immediate: true});
watch(() => inputConnector.value?.type || null, type => emit('connector', type), {immediate: true});

// The value to store: empty optional fields left out, so the server applies the defaults.
const submitValue = () => {
    const value = {...datasource.value};
    if (value.input) {
        value.input = {...value.input, options: sourceSchema.value
            ? valuesForSubmit(sourceSchema.value, props.level === 'workspace' ? value.input.options || {} : inputOptions.value)
            : value.input.options || {}};
    }
    if (value.output?.options && sinkSchema.value) {
        value.output = {...value.output, options: valuesForSubmit(sinkSchema.value, value.output.options)};
    }
    return value;
};

defineExpose({submitValue, missing});
</script>

<style scoped>
.datasource-editor {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.datasource-title {
    margin: 0 0 0.5rem;
}

.datasource-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
}

.datasource-field label {
    font-weight: 700;
}

.datasource-inline {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
}

.datasource-help {
    color: var(--p-text-muted-color);
}
</style>
